import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useApp, formatINR, optimizedImage } from "../store";
import { Check, AlertCircle, MapPin } from "lucide-react";

export default function Checkout() {
  const { API, cart, user, clearCart, authHeaders, setAuthOpen, settings } = useApp();
  const navigate = useNavigate();

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState("");
  const [saveAddress, setSaveAddress] = useState(false);

  const [form, setForm] = useState({
    customer_name: user?.name || "",
    customer_email: user?.email || "",
    customer_phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "India",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);
  const [paymentStep, setPaymentStep] = useState(null); // {order_id, total, details, items, form}
  const [utrRef, setUtrRef] = useState("");
  const [payMethod, setPayMethod] = useState("upi"); // "upi" | "bank_transfer"
  const [paymentNote, setPaymentNote] = useState("");

  // Coupon
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, percent } when valid
  const [couponMsg, setCouponMsg] = useState("");
  const applyCoupon = () => {
    setCouponMsg("");
    const code = (couponInput || "").trim().toUpperCase();
    const siteCode = (settings?.coupon_code || "").toUpperCase();
    if (settings?.coupon_active && code && code === siteCode) {
      setAppliedCoupon({ code: siteCode, percent: Number(settings.coupon_percent) || 0 });
      setCouponMsg("✓ Coupon applied!");
    } else {
      setAppliedCoupon(null);
      setCouponMsg("This code is not valid.");
    }
  };
  const removeCoupon = () => { setAppliedCoupon(null); setCouponInput(""); setCouponMsg(""); };

  // Load saved addresses for logged-in user, auto-fill default
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/addresses`, { headers: authHeaders }).then(r => {
      setSavedAddresses(r.data);
      const def = r.data.find(a => a.is_default) || r.data[0];
      if (def) applyAddress(def);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const applyAddress = (a) => {
    setSelectedAddrId(a.id);
    setForm(f => ({ ...f,
      customer_name: a.name || f.customer_name,
      customer_phone: a.phone || f.customer_phone,
      address_line1: a.address_line1, address_line2: a.address_line2 || "",
      city: a.city, state: a.state, postal_code: a.postal_code, country: a.country,
    }));
  };
  const clearAddressSelection = () => {
    setSelectedAddrId("");
    setForm(f => ({ ...f, address_line1: "", address_line2: "", city: "", state: "", postal_code: "" }));
  };

  const subtotal = cart.reduce((s, c) => s + c.price * c.quantity, 0);
  const gst = settings?.gst_rate ?? 0.18;
  const threshold = settings?.free_shipping_threshold ?? 5000;
  const flat = settings?.shipping_flat_rate ?? 250;
  const discount = appliedCoupon ? Math.round(subtotal * appliedCoupon.percent / 100) : 0;
  const tax = Math.round((subtotal - discount) * gst);
  const shipping = (subtotal - discount) >= threshold ? 0 : flat;
  const total = (subtotal - discount) + tax + shipping;

  if (cart.length === 0 && !success) {
    return <div className="pt-40 text-center text-[#8A8D8A] font-serif-display text-2xl">Your cart is empty.</div>;
  }

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const items = cart.map(c => ({ product_id: c.product_id, name: c.name, size: c.size, price: c.price, quantity: c.quantity }));
      const payload = { ...form, items, coupon_code: appliedCoupon?.code || "" };
      const { data } = await axios.post(`${API}/checkout/create-order`, payload, { headers: authHeaders });

      // Persist new address if user opted in
      if (user && saveAddress && !selectedAddrId && form.address_line1) {
        try {
          await axios.post(`${API}/addresses`, {
            label: "Saved at checkout",
            name: form.customer_name, phone: form.customer_phone,
            address_line1: form.address_line1, address_line2: form.address_line2,
            city: form.city, state: form.state, postal_code: form.postal_code,
            country: form.country, is_default: savedAddresses.length === 0,
          }, { headers: authHeaders });
        } catch (_) { /* non-fatal */ }
      }

      // Payment flow: bank / UPI / QR — show payment instructions, then collect UTR reference.
      const itemsSnap = [...cart];
      const formSnap = { ...form };
      // Do NOT clear the cart yet — customer might close without paying. Cart clears after UTR submission.
      setPaymentStep({
        order_id: data.order_id,
        total: data.amount,
        currency: data.currency,
        details: data.payment_details,
        summary: data.summary,
        items: itemsSnap,
        form: formSnap,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create order");
    } finally { setBusy(false); }
  };

  // Submit UTR / transaction reference after customer has paid via bank / UPI / QR.
  const submitPaymentReference = async () => {
    setErr("");
    if (!utrRef.trim() || utrRef.trim().length < 6) {
      setErr("Please enter a valid UTR / transaction reference (6+ characters).");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/checkout/submit-payment`, {
        order_id: paymentStep.order_id,
        utr_reference: utrRef.trim(),
        payment_method: payMethod,
        payer_name: form.customer_name,
        note: paymentNote,
      });
      clearCart();
      setSuccess({
        order_id: paymentStep.order_id,
        total: paymentStep.total,
        pending_verification: true,
        utr: utrRef.trim(),
        items: paymentStep.items,
        form: paymentStep.form,
      });
      setPaymentStep(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not submit payment reference");
    } finally { setBusy(false); }
  };

  if (success) {
    const ownerWA = (settings?.owner_whatsapp || "").replace(/[^\d]/g, "");
    let waLink = "";
    if (ownerWA && success.items && success.form) {
      const lines = [
        `🌹 *New Order from House of Absolutes KA website*`,
        ``,
        `*Order #${success.order_id.slice(0,8).toUpperCase()}*`,
        ``,
        `*Items:*`,
        ...success.items.map(i => `• ${i.name} — ${i.size} × ${i.quantity} — ${formatINR(i.price * i.quantity)}`),
        ``,
        `*Total:* ${formatINR(success.total)}`,
        ``,
        `*Customer:*`,
        `${success.form.customer_name}`,
        `📧 ${success.form.customer_email}`,
        `📞 ${success.form.customer_phone}`,
        ``,
        `*Shipping address:*`,
        success.form.address_line1,
        success.form.address_line2 || "",
        `${success.form.city}, ${success.form.state} ${success.form.postal_code}`,
        success.form.country,
        ...(success.form.notes ? [``, `*Note:* ${success.form.notes}`] : []),
      ].filter(Boolean).join("\n");
      waLink = `https://wa.me/${ownerWA}?text=${encodeURIComponent(lines)}`;
    }

    return (
      <div className="pt-40 pb-24 px-6 max-w-2xl mx-auto text-center" data-testid="order-success">
        <div className="w-16 h-16 rounded-full bg-[#3C6348]/10 mx-auto flex items-center justify-center mb-8">
          <Check size={32} className="text-[#3C6348]" strokeWidth={1.5} />
        </div>
        <div className="overline text-copper mb-4">Order Confirmed</div>
        <h1 className="font-serif-display text-5xl mb-4">Thank you<span className="italic text-[#8A8D8A]">.</span></h1>
        <p className="text-[#5C5E5C] mb-8 leading-relaxed">
          Your order <span className="font-mono text-xs">{success.order_id.slice(0, 8).toUpperCase()}</span> for {formatINR(success.total)} has been received.
          {success.pending_verification && (
            <span className="block mt-3 text-xs bg-[#F3EFE9] p-3 border border-[#E5E1D8]">
              <b className="text-copper">Payment reference recorded:</b> <span className="font-mono">{success.utr}</span>.<br/>
              We'll verify with our bank and dispatch within a few working hours. You'll receive an email confirmation.
            </span>
          )}
          <br/>A confirmation will reach your inbox shortly.
        </p>

        {waLink && (
          <div className="mb-8 bg-[#25D366]/10 border border-[#25D366]/30 p-6">
            <p className="text-sm text-[#5C5E5C] mb-4">
              <b>Help us prepare your order faster.</b> Tap below to share your order details with us on WhatsApp — takes one tap.
            </p>
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-4 font-medium hover:bg-[#1ebe5a] transition-colors"
              data-testid="send-whatsapp"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
              Send Order to House of Absolutes KA via WhatsApp
            </a>
            <p className="text-[10px] text-[#8A8D8A] mt-3">Opens WhatsApp with your order pre-filled. Just tap "Send".</p>
          </div>
        )}

        <div className="flex gap-3 justify-center">
          <button onClick={() => navigate("/catalog")} className="btn-primary" data-testid="continue-shopping">Continue shopping</button>
          {user && <button onClick={() => navigate("/orders")} className="btn-ghost">My orders</button>}
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-[1500px] mx-auto" data-testid="checkout-page">
      <div className="overline text-copper mb-4">Final Step</div>
      <h1 className="headline-xl text-[clamp(3rem,6vw,6rem)] mb-12">Checkout<span className="italic text-[#8A8D8A]">.</span></h1>

      {!user && (
        <div className="bg-sand border-l-2 border-copper p-4 mb-10 text-sm flex items-center justify-between">
          <span>You're checking out as a guest. <button className="text-copper underline" onClick={() => setAuthOpen(true)}>Sign in</button> to track your orders.</span>
        </div>
      )}

      <form onSubmit={submit} className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-10">
          <Section title="Contact">
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Full name" value={form.customer_name} onChange={v => setField("customer_name", v)} testid="ck-name" required />
              <Field label="Email" type="email" value={form.customer_email} onChange={v => setField("customer_email", v)} testid="ck-email" required />
              <Field label="Phone" value={form.customer_phone} onChange={v => setField("customer_phone", v)} testid="ck-phone" required />
            </div>
          </Section>
          <Section title="Shipping Address">
            {user && savedAddresses.length > 0 && (
              <div className="mb-8" data-testid="saved-addresses">
                <div className="overline text-[10px] mb-3 text-[#5C5E5C]">Choose a saved address</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {savedAddresses.map(a => (
                    <button
                      type="button"
                      key={a.id}
                      onClick={() => applyAddress(a)}
                      className={`text-left p-4 border transition-all ${selectedAddrId === a.id ? "border-[#1E1F1E] bg-sand" : "border-[#E5E1D8] hover:border-[#1E1F1E]"}`}
                      data-testid={`pick-addr-${a.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="overline text-[10px] text-copper">{a.label}</div>
                        {a.is_default && <span className="text-[9px] uppercase tracking-widest text-[#8A8D8A]">Default</span>}
                      </div>
                      <div className="text-sm font-serif-display mt-1">{a.name}</div>
                      <div className="text-xs text-[#5C5E5C] leading-snug mt-1">
                        {a.address_line1}, {a.city}, {a.state} {a.postal_code}
                      </div>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={clearAddressSelection}
                    className={`text-left p-4 border border-dashed transition-all ${!selectedAddrId ? "border-copper text-copper" : "border-[#E5E1D8] hover:border-copper"}`}
                    data-testid="new-address-option"
                  >
                    <div className="overline text-[10px]">+ Use a new address</div>
                  </button>
                </div>
                <Link to="/addresses" className="text-xs text-copper underline mt-3 inline-block">Manage address book →</Link>
              </div>
            )}
            <div className="space-y-6">
              <Field label="Address line 1" value={form.address_line1} onChange={v => setField("address_line1", v)} testid="ck-addr1" required />
              <Field label="Address line 2" value={form.address_line2} onChange={v => setField("address_line2", v)} testid="ck-addr2" />
              <div className="grid md:grid-cols-3 gap-6">
                <Field label="City" value={form.city} onChange={v => setField("city", v)} testid="ck-city" required />
                <Field label="State" value={form.state} onChange={v => setField("state", v)} testid="ck-state" required />
                <Field label="Postal code" value={form.postal_code} onChange={v => setField("postal_code", v)} testid="ck-zip" required />
              </div>
              <Field label="Country" value={form.country} onChange={v => setField("country", v)} testid="ck-country" required />
              {user && !selectedAddrId && (
                <label className="flex items-center gap-2 text-sm cursor-pointer text-[#5C5E5C]" data-testid="save-addr-toggle">
                  <input type="checkbox" checked={saveAddress} onChange={e => setSaveAddress(e.target.checked)} />
                  Save this address to my address book
                </label>
              )}
            </div>
          </Section>
          <Section title="Notes">
            <Field label="Order notes (optional)" value={form.notes} onChange={v => setField("notes", v)} testid="ck-notes" />
          </Section>
          {err && (
            <div className="text-xs text-[#9E3C3C] flex items-center gap-2" data-testid="checkout-error">
              <AlertCircle size={14} /> {err}
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className="sticky top-32 bg-sand p-8 space-y-5">
            <div className="overline text-copper">Order Review</div>
            <div className="divide-y divide-[#E5E1D8]">
              {cart.map(c => (
                <div key={c.product_id + c.size} className="flex gap-3 py-3 text-sm">
                  <img src={optimizedImage(c.image_url, 150)} alt={c.name} loading="lazy" decoding="async" className="w-14 h-16 object-cover" />
                  <div className="flex-1">
                    <div className="font-serif-display text-base">{c.name}</div>
                    <div className="overline text-[10px] text-[#8A8D8A]">{c.size} · ×{c.quantity}</div>
                  </div>
                  <div className="text-right text-sm">{formatINR(c.price * c.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#E5E1D8] pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#5C5E5C]">Subtotal</span><span>{formatINR(subtotal)}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-[#3C6348]">
                  <span>Discount{appliedCoupon ? ` (${appliedCoupon.code})` : ""}</span>
                  <span>- {formatINR(discount)}</span>
                </div>
              )}
              {gst > 0 && <div className="flex justify-between"><span className="text-[#5C5E5C]">GST {Math.round(gst * 100)}%</span><span>{formatINR(tax)}</span></div>}
              <div className="flex justify-between"><span className="text-[#5C5E5C]">Shipping</span><span>{shipping === 0 ? "Free" : formatINR(shipping)}</span></div>
            </div>

            {/* Coupon */}
            {settings?.coupon_active && (
              <div className="border-t border-[#E5E1D8] pt-4 space-y-2">
                {settings?.coupon_label && (
                  <div className="text-[11px] text-[#3C6348] leading-snug" data-testid="coupon-label">{settings.coupon_label}</div>
                )}
                {!appliedCoupon ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponInput}
                      onChange={e => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="Have a coupon? Enter code"
                      className="flex-1 border-b border-[#1E1F1E] bg-transparent py-2 text-sm tracking-widest focus:outline-none focus:border-copper"
                      data-testid="coupon-input"
                    />
                    <button
                      type="button"
                      onClick={applyCoupon}
                      className="overline text-xs text-copper hover:underline"
                      data-testid="coupon-apply"
                    >Apply</button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#3C6348]" data-testid="coupon-applied">✓ {appliedCoupon.code} applied</span>
                    <button type="button" onClick={removeCoupon} className="text-[#9E3C3C] underline" data-testid="coupon-remove">Remove</button>
                  </div>
                )}
                {couponMsg && !appliedCoupon && (
                  <div className="text-[11px] text-[#9E3C3C]" data-testid="coupon-msg">{couponMsg}</div>
                )}
              </div>
            )}

            <div className="border-t border-[#E5E1D8] pt-4 flex justify-between items-baseline">
              <span className="overline">Total</span>
              <span className="font-serif-display text-3xl" data-testid="checkout-total">{formatINR(total)}</span>
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="place-order">
              {busy ? "Processing…" : "Place Order"}
            </button>
            <p className="text-[10px] text-[#8A8D8A] text-center leading-relaxed">
              Secure manual payment via UPI or bank transfer. We verify every payment personally within working hours.
            </p>
          </div>
        </div>
      </form>

      {paymentStep && (
        <div className="fixed inset-0 bg-black/60 z-[120] flex items-start justify-center p-4 overflow-y-auto" data-testid="payment-step-modal">
          <div className="bg-[#FAF9F6] w-full max-w-2xl my-8 p-8 md:p-10">
            <div className="overline text-copper mb-2">Step 2 of 2 · Complete Payment</div>
            <h2 className="font-serif-display text-3xl mb-4">Pay {formatINR(paymentStep.total)}</h2>
            <p className="text-sm text-[#5C5E5C] mb-6 leading-relaxed">
              {paymentStep.details.instructions || "Scan the QR or transfer to the bank account below. Then enter the UTR / transaction reference so we can verify and dispatch."}
            </p>

            {/* Method toggle */}
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => setPayMethod("upi")}
                className={`px-4 py-2 text-xs overline border ${payMethod === "upi" ? "border-copper text-copper" : "border-[#E5E1D8] text-[#5C5E5C]"}`}
                data-testid="pay-method-upi"
              >UPI / QR</button>
              <button
                type="button"
                onClick={() => setPayMethod("bank_transfer")}
                className={`px-4 py-2 text-xs overline border ${payMethod === "bank_transfer" ? "border-copper text-copper" : "border-[#E5E1D8] text-[#5C5E5C]"}`}
                data-testid="pay-method-bank"
              >Bank Transfer</button>
            </div>

            {payMethod === "upi" && (
              <div className="mb-6">
                {paymentStep.details.qr_url ? (
                  <div className="border border-[#E5E1D8] bg-white overflow-hidden mb-4">
                    <img
                      src={paymentStep.details.qr_url}
                      alt="Payment options — scan or use bank details"
                      className="w-full h-auto block"
                      data-testid="payment-qr-image"
                    />
                  </div>
                ) : (
                  <div className="border border-dashed border-[#E5E1D8] p-8 text-center text-xs text-[#8A8D8A] mb-4">
                    QR code not uploaded yet.<br/>Please use the UPI ID or bank transfer instead.
                  </div>
                )}
                {paymentStep.details.upi_id && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-[#F3EFE9] p-4">
                      <div className="overline text-xs text-[#8A8D8A] mb-2">UPI ID (tap to copy)</div>
                      <div className="font-mono text-sm break-all" data-testid="payment-upi-id">{paymentStep.details.upi_id}</div>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(paymentStep.details.upi_id); }}
                        className="mt-2 text-xs text-copper underline"
                      >Copy UPI ID</button>
                    </div>
                    <div className="text-xs text-[#5C5E5C] leading-relaxed p-4">
                      Amount to pay: <b>{formatINR(paymentStep.total)}</b><br/>
                      Reference / note: <span className="font-mono">Order {paymentStep.order_id.slice(0, 8).toUpperCase()}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {payMethod === "bank_transfer" && (
              <div className="bg-[#F3EFE9] p-6 mb-6 space-y-3 text-sm" data-testid="payment-bank-block">
                <BankRow label="Account name" value={paymentStep.details.bank_account_name} />
                <BankRow label="Bank" value={paymentStep.details.bank_name} />
                <BankRow label="Account number" value={paymentStep.details.bank_account_number} mono />
                <BankRow label="IFSC" value={paymentStep.details.bank_ifsc} mono />
                <BankRow label="Amount" value={formatINR(paymentStep.total)} />
                <BankRow label="Reference" value={`Order ${paymentStep.order_id.slice(0, 8).toUpperCase()}`} mono />
              </div>
            )}

            {/* UTR entry */}
            <div className="border-t border-[#E5E1D8] pt-6">
              <label className="overline text-[10px] block mb-2">UTR / Transaction reference *</label>
              <input
                type="text"
                value={utrRef}
                onChange={e => setUtrRef(e.target.value)}
                placeholder="e.g. 234512345678 or UPI reference ID"
                className="input-underline w-full mb-3"
                data-testid="payment-utr-input"
                autoFocus
              />
              <label className="overline text-[10px] block mb-2 mt-4">Note (optional)</label>
              <textarea
                value={paymentNote}
                onChange={e => setPaymentNote(e.target.value)}
                rows={2}
                placeholder="Any additional info you want us to know"
                className="input-underline w-full"
                data-testid="payment-note-input"
              />
              {err && <p className="text-[#9E3C3C] text-xs mt-3" data-testid="payment-err">✗ {err}</p>}
              <div className="flex justify-between items-center mt-6">
                <button
                  type="button"
                  onClick={() => { setPaymentStep(null); setUtrRef(""); setPaymentNote(""); }}
                  disabled={busy}
                  className="text-xs text-[#8A8D8A] underline"
                >
                  Cancel — I'll pay later
                </button>
                <button
                  type="button"
                  onClick={submitPaymentReference}
                  disabled={busy || !utrRef.trim()}
                  className="btn-primary disabled:opacity-40"
                  data-testid="payment-submit-utr"
                >
                  {busy ? "Submitting…" : "I've paid — Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BankRow({ label, value, mono }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    if (!value) return;
    navigator.clipboard.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="overline text-[10px] text-[#8A8D8A]">{label}</span>
      <span className="flex items-baseline gap-3">
        <span className={mono ? "font-mono text-sm" : "text-sm"}>{value || "—"}</span>
        {value && (
          <button type="button" onClick={copy} className="text-[10px] text-copper underline">
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="overline text-copper mb-6">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, value, onChange, testid, required, type = "text" }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-2">{label}{required && " *"}</label>
      <input type={type} className="input-underline" value={value} onChange={e => onChange(e.target.value)} required={required} data-testid={testid} />
    </div>
  );
}
